import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useState, useRef, useEffect } from 'react';
import CharacterCounter from 'apps/interactions/components/character-counter';
import { TextInput, Radio, ListboxMultiselect, ModalConfirm, Loading } from '@evertrue/et-components';
import { AddToFolderSchema } from './schema/add-to-folder-schema';
import EverTrue from 'app';
import { useFolders } from 'apps/folder/hooks/use-folders';

const AddToFolderConfirmModal = ({
  list = {},
  label = 'Add to Existing Folder(s)',
  folderType = 'STATIC',
  requestHide = () => {},
}) => {
  const { folders, loading, createFolder, addListsToFolder, updateListCountState, addListsToFolderAndUpdateState } =
    useFolders(folderType);

  const [filteredOptions, setFilteredOptions] = useState([]);
  const searchTimeoutRef = useRef(null);

  // Update folderOptions when folders are loaded
  useEffect(() => {
    if (!loading) {
      const folderOptions = folders.map(({ folder }) => ({
        label: folder.title,
        value: folder.id,
      }));
      setFilteredOptions(folderOptions);
    }
  }, [folders, loading]); // Run effect when folders or loading changes

  const handleSearch = (search) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (search === '') {
        setFilteredOptions(
          folders.map(({ folder }) => ({
            label: folder.title,
            value: folder.id,
          }))
        );
      } else {
        const filtered = filteredOptions.filter((option) => option.label.toLowerCase().includes(search.toLowerCase()));
        setFilteredOptions(filtered);
      }
    }, 500);
  };

  // Either create a new folder and add lists to it, or add lists to existing folders, then update state and close modal
  const handleAddListsToFolders = (result) => {
    if (result.type === 'create') {
      createFolder(result.folderName)
        .then((folder) => addListsToFolderAndUpdateState(folder, [list?.id]))
        .then(() => {
          //mixpanel create folder
          const actionType = folderType === 'DYNAMIC' ? 'saved_search_action' : 'list_action';
          EverTrue.track.set(actionType, { type: 'create_folder' });
          //mixpanel add to folder
          EverTrue.track.set(actionType, { type: 'add_to_existing_folder' });

          EverTrue.Alert.success(`${list.name} added to ${result.folderName}`);
        })
        .catch((error) => {
          const errorMessage =
            folderType === 'DYNAMIC'
              ? 'Error creating Folder and adding Saved Search(s)'
              : 'Error creating Folder and adding List(s)';
          EverTrue.Alert.error(errorMessage);
        });
    } else if (result.type === 'existing') {
      const { folders } = result;
      const folderIds = folders.map((folder) => folder.value);
      addListsToFolder(folderIds, [list?.id])
        .then((res) => {
          if (!res || !Array.isArray(res)) return;
          //mixpanel add to existing folder
          const actionType = folderType === 'DYNAMIC' ? 'saved_search_action' : 'list_action';
          EverTrue.track.set(actionType, { type: 'add_to_existing_folder' });

          EverTrue.Alert.success(`${list.name} added to Folder(s)`);
          updateListCountState(res.flat()); // API is returning nested array when adding to multiple folders
        })
        .catch((error) => {
          const errorMessage =
            folderType === 'DYNAMIC' ? 'Error adding Saved Search to Folder(s)' : 'Error adding List to Folder(s)';
          EverTrue.Alert.error(errorMessage);
        });
    }
  };

  return (
    <Formik
      initialValues={{
        action: 'existing',
        selectedFolders: [],
        folderName: '',
      }}
      validationSchema={AddToFolderSchema}
      onSubmit={(values) => {
        if (values.action === 'create') {
          handleAddListsToFolders({ type: 'create', folderName: values.folderName });
        } else {
          handleAddListsToFolders({ type: 'existing', folders: values.selectedFolders });
        }
        requestHide();
      }}
    >
      {({ values, errors, touched, handleChange, handleSubmit, setFieldValue }) => (
        <ModalConfirm
          header={folderType === 'DYNAMIC' ? 'Add Saved Search to Folder(s)' : 'Add List to Folder(s)'}
          onCancel={requestHide}
          onSubmit={handleSubmit}
          disableSave={
            (values.action === 'existing' && values.selectedFolders.length === 0) ||
            (values.action === 'create' && !values.folderName.trim())
          }
        >
          <div style={{ padding: '8px 16px 8px 16px', margin: '8px 0 8px 0' }}>
            <Radio
              name="action"
              value="existing"
              label={label}
              checked={values.action === 'existing'}
              onChange={() => setFieldValue('action', 'existing')}
            />
            <div style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <ListboxMultiselect
                size="medium"
                emptyValue="Select Folder(s)"
                label="Select Folder(s)"
                options={filteredOptions}
                onChange={(selected) => setFieldValue('selectedFolders', selected)}
                values={values.selectedFolders}
                disabled={loading || values.action !== 'existing'}
                searchable={true}
                onSearch={handleSearch}
                showInfoBar={true}
                error={touched.selectedFolders && errors.selectedFolders}
              />
              {loading && <Loading />}
            </div>

            <Radio
              name="action"
              value="create"
              label="Create New Folder"
              checked={values.action === 'create'}
              onChange={() => setFieldValue('action', 'create')}
            />
            <div style={{ marginLeft: '24px' }}>
              <TextInput
                placeholder="Name Your Folder"
                name="folderName"
                value={values.folderName}
                onChange={handleChange('folderName')}
                maxLength={30}
                required={true}
                disabled={values.action !== 'create'}
                error={touched.folderName && errors.folderName}
              />
              <CharacterCounter limit={30} count={(values.folderName || '').length} />
            </div>
          </div>
        </ModalConfirm>
      )}
    </Formik>
  );
};

AddToFolderConfirmModal.propTypes = {
  list: PropTypes.object.isRequired,
  label: PropTypes.string,
  folderType: PropTypes.string,
  requestHide: PropTypes.func,
};

export default AddToFolderConfirmModal;
